(() => {
  const MESSAGE_TYPE = '3mf_customized';
  // Kept in step with src/shopify/notifyParent.ts in the editor.
  const READY_TYPE = '3mf_ready';
  const CLOSE_TYPE = '3mf_close';

  function closestProductForm(el) {
    const scoped = el.closest('form[action*="/cart/add"]');
    if (scoped) return scoped;

    // A Dawn-family product page carries SEVERAL forms posting to /cart/add:
    // the buy form, an installment form, sometimes quick-add. Taking the first
    // one in the document can pick a form that is never submitted, and
    // properties written into it silently never reach the cart. The real buy
    // form is the one inside <product-form> with the add-to-cart button.
    const forms = Array.from(document.querySelectorAll('form[action*="/cart/add"]'))
      .filter(function (f) { return !/installment/i.test(f.id || ''); });
    return (
      forms.find(function (f) { return f.closest('product-form') && f.querySelector('[name="add"]'); }) ||
      forms.find(function (f) { return f.querySelector('[name="add"]'); }) ||
      forms[0] ||
      null
    );
  }

  function ensureHiddenInput(form, name) {
    let input = form.querySelector(`input[name="${CSS.escape(name)}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      // Belt and braces: the form attribute ties the input to the buy form by
      // id, so it serialises with it even if the node itself sits elsewhere.
      if (form.id) input.setAttribute('form', form.id);
      form.appendChild(input);
    }
    return input;
  }

  // The filament list arrives over postMessage, so treat it as untrusted:
  // plain strings only, bounded in count and length.
  function sanitizeFilaments(raw) {
    if (!Array.isArray(raw)) return [];
    return raw
      .slice(0, 8)
      .filter(function (f) {
        return f && typeof f.label === 'string' && typeof f.value === 'string';
      })
      .map(function (f) {
        return { label: f.label.slice(0, 60), value: f.value.slice(0, 120) };
      });
  }

  function parseJsonAttribute(value) {
    const trimmed = (value || '').trim();
    if (!trimmed) return null;
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) && parsed.length ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // Options apps (Globo and friends) often submit the cart line themselves,
  // building the request from their own fields — anything written into the
  // form as hidden inputs is never read. So the request itself is the last
  // line of defence: every /cart/add call that is for OUR product gets the
  // design properties merged in, whichever code makes the call. Existing keys
  // are never overwritten, and other products are matched by variant id and
  // left alone.
  // ---------------------------------------------------------------------------

  // One registration per customizer on the page: how to recognise its product
  // (the buy form's selected variant) and what to attach.
  const cartAddRegistrations = [];

  function registrationForVariant(variantId) {
    const id = String(variantId || '');
    for (const reg of cartAddRegistrations) {
      const formVariant = reg.getVariantId();
      // A missing id on either side means "cannot tell": match only when the
      // page has exactly one registered customizer, which for a product page
      // it always does.
      if (id && formVariant && id !== String(formVariant)) continue;
      return reg;
    }
    return null;
  }

  function propertiesForVariant(variantId) {
    const reg = registrationForVariant(variantId);
    return reg ? reg.getProperties() : null;
  }

  // The variant a cart/add body is for, across the shapes the API takes.
  // Null means it could not be read, which matches everything.
  function peekVariantId(body) {
    try {
      if (body instanceof FormData || body instanceof URLSearchParams) {
        return body.get('id');
      }
      if (typeof body === 'string' && body) {
        const trimmed = body.trim();
        if (trimmed.startsWith('{')) {
          const payload = JSON.parse(trimmed);
          const items = Array.isArray(payload.items) ? payload.items : [payload];
          return items[0] && items[0].id != null ? items[0].id : null;
        }
        return new URLSearchParams(trimmed).get('id');
      }
    } catch (e) {
      // Unreadable body: treat as unknown.
    }
    return null;
  }

  // Whether this add must be refused because the product requires a saved
  // design and there is none. Returns the message to show, or null to proceed.
  function designRequiredMessage(variantId) {
    const reg = registrationForVariant(variantId);
    if (!reg || !reg.requiresDesign || !reg.requiresDesign()) return null;
    return 'Please save your custom hood design before adding this to the cart.';
  }

  function mergeIntoFormDataLike(body, props) {
    let added = 0;
    Object.keys(props).forEach(function (key) {
      const name = 'properties[' + key + ']';
      if (!body.has(name)) {
        body.append(name, props[key]);
        added++;
      }
    });
    return added;
  }

  // Merges the design properties into a JSON /cart/add payload, which is
  // either one item or {items:[...]}. Each item is matched by its own variant.
  function mergeIntoJsonPayload(payload) {
    const items = Array.isArray(payload.items) ? payload.items : [payload];
    items.forEach(function (item) {
      if (!item || typeof item !== 'object') return;
      const wanted = propertiesForVariant(item.id);
      if (!wanted) return;
      item.properties = item.properties || {};
      Object.keys(wanted).forEach(function (key) {
        if (!(key in item.properties)) {
          item.properties[key] = wanted[key];
        }
      });
    });
  }

  function isCartAddUrl(url) {
    return /\/cart\/add(\.js)?([?#]|$)/.test(String(url || ''));
  }

  // Rewrites one request body (string | FormData | URLSearchParams). Returns
  // the body to use — the original when there is nothing to do. Anything
  // unexpected is left untouched: a failed merge must never break add-to-cart.
  function withDesignProperties(body) {
    try {
      if (body instanceof FormData || body instanceof URLSearchParams) {
        const props = propertiesForVariant(body.get('id'));
        if (props) mergeIntoFormDataLike(body, props);
        return body;
      }
      if (typeof body === 'string' && body) {
        const trimmed = body.trim();
        if (trimmed.startsWith('{')) {
          const payload = JSON.parse(trimmed);
          mergeIntoJsonPayload(payload);
          return JSON.stringify(payload);
        }
        const params = new URLSearchParams(trimmed);
        const props = propertiesForVariant(params.get('id'));
        if (props) mergeIntoFormDataLike(params, props);
        return params.toString();
      }
    } catch (e) {
      // Fall through to the untouched body.
    }
    return body;
  }

  function installCartAddInterceptor() {
    if (window.__threeMfCartHooked) return;
    window.__threeMfCartHooked = true;

    const realFetch = window.fetch;
    window.fetch = function (input, init) {
      try {
        const url = typeof input === 'string' ? input : input && input.url;
        if (isCartAddUrl(url) && init && init.body != null) {
          // A product that requires a saved design refuses the add outright,
          // in the same shape Shopify uses for cart errors, so whichever app
          // made the call shows the message through its own error handling.
          const refusal = designRequiredMessage(peekVariantId(init.body));
          if (refusal) {
            return Promise.resolve(
              new Response(
                JSON.stringify({ status: 422, message: 'Cart Error', description: refusal }),
                { status: 422, headers: { 'Content-Type': 'application/json' } }
              )
            );
          }
          init = Object.assign({}, init, { body: withDesignProperties(init.body) });
        }
      } catch (e) {
        // Never break the request over the merge.
      }
      return realFetch.call(this, input, init);
    };

    const realOpen = XMLHttpRequest.prototype.open;
    const realSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__threeMfCartAdd = isCartAddUrl(url);
      return realOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      try {
        if (this.__threeMfCartAdd && body != null) {
          // XHR cannot be answered with a synthetic response, so a refused add
          // is simply never sent; the app sees a network error, and the page's
          // own status line says why.
          if (designRequiredMessage(peekVariantId(body))) {
            const xhr = this;
            setTimeout(function () {
              xhr.dispatchEvent(new ProgressEvent('error'));
            }, 0);
            return undefined;
          }
          body = withDesignProperties(body);
        }
      } catch (e) {
        // Never break the request over the merge.
      }
      return realSend.call(this, body);
    };
  }

  function init(container) {
    const baseUrl = container.getAttribute('data-customizer-url');
    if (!baseUrl) return;

    const form = closestProductForm(container);
    const status = container.querySelector('.three-mf-customizer__status');
    if (!form) {
      if (status) status.textContent = 'Customizer: product form not found.';
      return;
    }

    const caption = container.querySelector('.three-mf-customizer__caption');
    const image = container.querySelector('.three-mf-customizer__image');
    const openButton = container.querySelector('.three-mf-customizer__open');
    const modal = container.querySelector('.three-mf-customizer__modal');
    const iframe = container.querySelector('.three-mf-customizer__iframe');
    const closeButton = container.querySelector('.three-mf-customizer__close');

    const shopDomain = container.getAttribute('data-shop-domain');
    const modelUrl = container.getAttribute('data-model-url');
    const filesJson = container.getAttribute('data-files-json');
    const defaultFileId = container.getAttribute('data-default-file-id');
    const promptText =
      container.getAttribute('data-prompt-text') || 'Customize your hood here';
    const productId = container.getAttribute('data-product-id') || 'product';
    const storageKey = `3mf-design:${productId}`;

    // Line-item properties travel with the cart into the order automatically,
    // so the saved design shows up on the order with no extra app work.
    // "Design ID" is customer-visible; the _-prefixed ones are hidden on the
    // storefront but still visible in the admin.
    // The printable 3MF is paid content, so its download URL is never put on
    // the page. Only the design id (and an internal file id) travel with the
    // order; you resolve the actual file from the GUID in the admin.
    //
    // Design ID is deliberately customer-visible: Shopify prints properties
    // without a leading underscore in the cart and checkout order summary, so
    // the customer sees their code at checkout with no app involved. Everything
    // else is prefixed to keep raw URLs out of that summary while staying
    // readable from Liquid and the admin.
    const designIdInput = ensureHiddenInput(form, 'properties[Design ID]');
    const previewInput = ensureHiddenInput(form, 'properties[_Design Preview]');
    const fileIdInput = ensureHiddenInput(form, 'properties[_3MF File ID]');
    const filenameInput = ensureHiddenInput(form, 'properties[_3MF Filename]');

    let customizerOrigin = null;
    try {
      customizerOrigin = new URL(baseUrl, window.location.href).origin;
    } catch (e) {
      customizerOrigin = null;
    }

    function setStatus(text) {
      if (status) status.textContent = text || '';
    }

    // One property per filament, without an underscore, so the colours to load
    // are readable straight off the order (and by the customer at checkout).
    // Old inputs are removed rather than reused: a re-save may carry different
    // labels, and a stale "Filament 4" from the previous save must not linger.
    function applyFilaments(filaments) {
      form
        .querySelectorAll('input[data-three-mf-filament]')
        .forEach(function (el) { el.remove(); });
      (filaments || []).forEach(function (f) {
        const input = ensureHiddenInput(form, 'properties[' + f.label + ']');
        input.setAttribute('data-three-mf-filament', '1');
        input.value = f.value;
      });
    }

    function applyDesign(design, options) {
      const opts = options || {};
      designIdInput.value = design.guid || '';
      previewInput.value = design.previewUrl || '';
      fileIdInput.value = design.fileId || '';
      filenameInput.value = design.filename || '';
      applyFilaments(sanitizeFilaments(design.filaments));

      if (caption) {
        caption.textContent = design.guid ? `Design ${design.guid}` : promptText;
      }
      if (image && design.previewUrl) {
        image.src = design.previewUrl;
        image.removeAttribute('srcset');
      }
      if (opts.announce) {
        setStatus('Design saved and attached to this item.');
      }

      // One console line saying exactly where the properties went, so "they
      // are not on my cart line" is diagnosable at a glance.
      try {
        console.info(
          '[3mf-customizer] design attached to form#' + (form.id || '(no id)'),
          Array.from(form.querySelectorAll('input[name^="properties["]')).map(function (i) {
            return i.name + '=' + i.value;
          })
        );
      } catch (e) {
        // Logging must never break the save.
      }

      try {
        window.localStorage.setItem(storageKey, JSON.stringify(design));
      } catch (e) {
        // Storage may be unavailable (private mode); the form still carries it.
      }
    }

    // Restore a design the customer made earlier in this browser so reopening
    // the editor continues from their work instead of the blank template.
    let currentDesign = null;
    try {
      currentDesign = JSON.parse(window.localStorage.getItem(storageKey) || 'null');
    } catch (e) {
      currentDesign = null;
    }
    if (currentDesign && currentDesign.guid) {
      applyDesign(currentDesign, { announce: false });
    } else if (caption) {
      caption.textContent = promptText;
    }

    // The properties an add-to-cart of this product must carry, mirroring what
    // applyDesign writes into the form. Options apps that build the cart
    // request themselves get these merged in by the interceptor below.
    function designProperties() {
      if (!currentDesign || !currentDesign.guid) return null;
      const props = {
        'Design ID': currentDesign.guid,
        '_Design Preview': currentDesign.previewUrl || '',
        '_3MF File ID': currentDesign.fileId || '',
        '_3MF Filename': currentDesign.filename || '',
      };
      sanitizeFilaments(currentDesign.filaments).forEach(function (f) {
        props[f.label] = f.value;
      });
      return props;
    }

    const requireDesign = container.getAttribute('data-require-design') === 'true';

    function designMissing() {
      return requireDesign && !(currentDesign && currentDesign.guid);
    }

    // Greys out every way of buying until the design is saved: the form's own
    // submit buttons, dynamic checkout (Buy it now), and whatever buttons an
    // options or pre-order app renders inside the form. Those apps re-render
    // their buttons, so the observer keeps re-applying to whatever is there.
    function updateBuyButtons() {
      if (!requireDesign) return;
      const blocked = designMissing();
      const buttons = [];
      form
        .querySelectorAll('button[type="submit"], [name="add"], input[type="submit"]')
        .forEach(function (b) { buttons.push(b); });
      document
        .querySelectorAll('.shopify-payment-button button')
        .forEach(function (b) { buttons.push(b); });
      buttons.forEach(function (b) {
        if (blocked) {
          b.setAttribute('disabled', 'disabled');
          b.setAttribute('data-three-mf-blocked', '1');
          b.setAttribute('title', 'Save your custom design first');
        } else if (b.getAttribute('data-three-mf-blocked')) {
          b.removeAttribute('disabled');
          b.removeAttribute('data-three-mf-blocked');
          b.removeAttribute('title');
        }
      });
      if (blocked) {
        setStatus('Save your custom hood design to enable adding to cart.');
      }
    }

    if (requireDesign) {
      updateBuyButtons();
      let buttonSweep = null;
      const observer = new MutationObserver(function () {
        // Coalesce bursts of app re-renders into one sweep, and never react to
        // our own attribute writes (childList only).
        if (buttonSweep != null) return;
        buttonSweep = window.setTimeout(function () {
          buttonSweep = null;
          updateBuyButtons();
        }, 50);
      });
      observer.observe(form, { childList: true, subtree: true });
    }

    cartAddRegistrations.push({
      getVariantId: function () {
        const input = form.querySelector('[name="id"]');
        return input ? input.value : null;
      },
      getProperties: designProperties,
      requiresDesign: designMissing,
    });
    installCartAddInterceptor();

    function buildEditorUrl() {
      const url = new URL(baseUrl, window.location.href);
      if (shopDomain) url.searchParams.set('shop', shopDomain);
      url.searchParams.set('sendToParent', '1');
      url.searchParams.set('parentOrigin', window.location.origin);

      const params = new URLSearchParams();
      const parsedFiles = parseJsonAttribute(filesJson);
      if (!parsedFiles && (filesJson || '').trim()) {
        // The metafield had something in it but nothing came out. Say so rather
        // than quietly falling back to the single model, which looks like the
        // template picker having vanished for no reason.
        setStatus('Customizer: could not read the template list from the product metafield.');
        console.warn('[3mf-customizer] unreadable data-files-json:', filesJson);
      }
      if (parsedFiles) {
        params.set('files', JSON.stringify(parsedFiles));
        if (defaultFileId !== null && String(defaultFileId).trim() !== '') {
          params.set('fileId', String(defaultFileId).trim());
        }
      } else if (modelUrl) {
        params.set('url', modelUrl);
      } else {
        return null;
      }

      // Reopen on the customer's own saved design when there is one.
      if (currentDesign && currentDesign.guid) {
        params.set('guid', currentDesign.guid);
      }

      url.hash = `#/editor?${params.toString()}`;
      return url.toString();
    }

    // The editor puts Close in its own top bar and asks us to shut the modal,
    // so this page's button is a fallback for the editor never loading. It is
    // revealed only if no "ready" message arrives — two close buttons at once
    // is how the old one ended up sitting on top of the editor's Save.
    const READY_GRACE_MS = 6000;
    let editorReady = false;
    let readyTimer = null;

    function openModal() {
      const editorUrl = buildEditorUrl();
      if (!editorUrl) {
        setStatus('Customizer: no 3MF template configured.');
        return;
      }
      // Load the editor only when it is actually opened.
      if (iframe.getAttribute('src') !== editorUrl) {
        iframe.setAttribute('src', editorUrl);
      }
      modal.hidden = false;
      document.body.style.overflow = 'hidden';

      if (closeButton && !editorReady) {
        closeButton.hidden = true;
        if (readyTimer) window.clearTimeout(readyTimer);
        readyTimer = window.setTimeout(() => {
          if (!editorReady) closeButton.hidden = false;
        }, READY_GRACE_MS);
      }
    }

    function closeModal() {
      modal.hidden = true;
      document.body.style.overflow = '';
      if (readyTimer) {
        window.clearTimeout(readyTimer);
        readyTimer = null;
      }
    }

    if (openButton) openButton.addEventListener('click', openModal);
    if (closeButton) closeButton.addEventListener('click', closeModal);
    if (modal) {
      modal.addEventListener('click', (event) => {
        if (event.target === modal) closeModal();
      });
    }
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && modal && !modal.hidden) closeModal();
    });

    window.addEventListener('message', (event) => {
      // Only trust messages coming from the customizer app itself.
      if (customizerOrigin && event.origin !== customizerOrigin) return;

      const data = event.data;
      if (!data || !data.type) return;

      if (data.type === READY_TYPE) {
        editorReady = true;
        if (readyTimer) {
          window.clearTimeout(readyTimer);
          readyTimer = null;
        }
        if (closeButton) closeButton.hidden = true;
        return;
      }

      if (data.type === CLOSE_TYPE) {
        closeModal();
        return;
      }

      if (data.type !== MESSAGE_TYPE) return;

      currentDesign = {
        guid: data.guid || '',
        previewUrl: data.previewUrl || '',
        fileId: data.fileId || '',
        filename: data.filename || '',
        filaments: sanitizeFilaments(data.filaments),
      };
      applyDesign(currentDesign, { announce: true });
      updateBuyButtons();
      closeModal();
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.three-mf-customizer').forEach(init);
  });
})();
